import { ApiActions } from '@orientaction/api-actions';
import { useLanguage } from '@orientaction/hooks';
import { apiState } from '@orientaction/reducers';
import { GB_PREFIX, getFromLS, shouldHideCompass } from '@orientaction/utils';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

type NavItem = {
  label: string;
  tabIndex: number;
  hidden?: boolean;
};

type UseHeaderNavOptions = {
  isAdmin: boolean;
  isRoot?: boolean;
  isJobLayout?: boolean;
  service?: { id?: number };
};

export const useHeaderNav = ({
  isAdmin,
  isRoot = false,
  isJobLayout = false,
  service,
}: UseHeaderNavOptions) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const { tabLayout } = useSelector(apiState);
  const { language } = useLanguage();
  const currentUser = JSON.parse(getFromLS('userInfos') || '{}');

  const [openExit, setOpenExit] = useState(false);
  const [nextRoute, setNextRoute] = useState(0);

  const userNavItems: NavItem[] = useMemo(
    () => [
      { label: 'Accueil', tabIndex: 0 },
      { label: 'Faire un test', tabIndex: 1 },
      { label: 'Mes résultats', tabIndex: 2 },
      { label: 'Mes ressources', tabIndex: 3 },
      { label: 'Mon profil', tabIndex: 4, hidden: shouldHideCompass(currentUser) },
    ],
    [currentUser],
  );

  const adminNavItems: NavItem[] = useMemo(() => {
    if (isJobLayout) {
      return [
        { label: 'Accueil', tabIndex: 0 },
        { label: 'Candidats', tabIndex: 1 },
        { label: 'Annonces', tabIndex: 2 },
      ];
    }

    const usersResultsLabel =
      currentUser?.company_user_role?.id === 4
        ? 'Liste des bénéficiaires'
        : 'Utilisateurs et résultats';

    if (currentUser.isRGPD) {
      return [
        { label: usersResultsLabel, tabIndex: 0 },
        { label: 'Espaces entreprises', tabIndex: 1 },
      ];
    }

    return [
      { label: usersResultsLabel, tabIndex: 0 },
      { label: 'Admin. des ressources', tabIndex: 1, hidden: !isRoot },
      { label: 'Administration des tests', tabIndex: 2, hidden: !isRoot },
      { label: 'Espaces entreprises', tabIndex: 3, hidden: !isRoot },
      { label: 'Statistiques', tabIndex: 4, hidden: !isRoot },
      { label: 'Leads', tabIndex: 5, hidden: !isRoot },
    ];
  }, [currentUser.isRGPD, currentUser?.company_user_role?.id, isJobLayout, isRoot]);

  const navItems = (isAdmin ? adminNavItems : userNavItems).filter(item => !item.hidden);

  useEffect(() => {
    if (isAdmin) {
      if (!currentUser.isRGPD) {
        if (pathname.includes('/admin/leads')) {
          dispatch(ApiActions.setTabLayout(5 as any));
        } else if (pathname.includes('/admin/statistics')) {
          dispatch(ApiActions.setTabLayout(4 as any));
        } else if (pathname.includes('/admin/test')) {
          dispatch(ApiActions.setTabLayout(2 as any));
        } else if (pathname.includes('/admin/business-spaces')) {
          dispatch(ApiActions.setTabLayout(3 as any));
        } else if (pathname.includes('/admin/game') || pathname.includes('/admin/ebook')) {
          dispatch(ApiActions.setTabLayout(1 as any));
        } else if (pathname.includes('/admin/emploi/annonces')) {
          dispatch(ApiActions.setTabLayout(2 as any));
        } else if (pathname.includes('/admin/emploi/candidats')) {
          dispatch(ApiActions.setTabLayout(1 as any));
        } else if (pathname.includes('/admin/emploi/board')) {
          dispatch(ApiActions.setTabLayout(0 as any));
        } else if (pathname.includes('/admin/emploi/annonce/')) {
          dispatch(ApiActions.setTabLayout(2 as any));
        } else if (pathname.includes('/admin/emploi/detail-canditat/')) {
          dispatch(ApiActions.setTabLayout(1 as any));
        } else {
          dispatch(ApiActions.setTabLayout(0 as any));
        }
      } else if (pathname.includes('/admin/business-spaces')) {
        dispatch(ApiActions.setTabLayout(1 as any));
      } else {
        dispatch(ApiActions.setTabLayout(0 as any));
      }
      return;
    }

    if (pathname.includes('/ma-boussole') || pathname.includes('/my-compass')) {
      dispatch(ApiActions.setTabLayout(4 as any));
      return;
    }

    switch (pathname) {
      case '/main':
        dispatch(ApiActions.setTabLayout(0 as any));
        break;
      case '/user/tests':
        dispatch(ApiActions.setTabLayout(1 as any));
        break;
      case '/mes-resultats':
      case '/en/my-results':
        dispatch(ApiActions.setTabLayout(2 as any));
        break;
      case '/mes-ressources':
      case '/en/my-resources':
        dispatch(ApiActions.setTabLayout(3 as any));
        break;
      default:
        if (pathname.includes('/resultats')) {
          dispatch(ApiActions.setTabLayout(2 as any));
        }
        break;
    }
  }, [pathname, isAdmin, currentUser.isRGPD, dispatch]);

  const navigateToTab = (newValue: number) => {
    dispatch(ApiActions.setTabLayout(newValue as any));

    if (isAdmin) {
      if (!currentUser.isRGPD) {
        switch (newValue) {
          case 0:
            history.push(isJobLayout ? '/admin/emploi/board' : '/admin/users-resultats');
            break;
          case 1:
            history.push(isJobLayout ? '/admin/emploi/candidats' : '/admin/game/list');
            break;
          case 2:
            history.push(isJobLayout ? '/admin/emploi/annonces' : '/admin/test');
            break;
          case 3:
            history.push('/admin/business-spaces');
            break;
          case 4:
            history.push('/admin/statistics');
            break;
          case 5:
            history.push('/admin/leads');
            break;
          default:
            break;
        }
      } else {
        switch (newValue) {
          case 0:
            history.push('/admin/users-resultats');
            break;
          case 1:
            history.push('/admin/business-spaces');
            break;
          default:
            break;
        }
      }
      return;
    }

    switch (newValue) {
      case 0:
        history.push('/main');
        break;
      case 1:
        history.push('/user/tests');
        break;
      case 2:
        history.push(language === GB_PREFIX ? '/en/my-results' : '/mes-resultats');
        break;
      case 3:
        history.push(language === GB_PREFIX ? '/en/my-resources' : '/mes-ressources');
        break;
      case 4:
        history.push(
          language === GB_PREFIX
            ? `/en/my-compass/${new Date().getFullYear()}`
            : `/ma-boussole/${new Date().getFullYear()}`,
        );
        break;
      default:
        break;
    }
  };

  const handleNavClick = (tabIndex: number) => {
    setNextRoute(tabIndex);

    if (
      isAdmin &&
      (pathname.includes('game/edit-page') || pathname.includes('ebook/edit'))
    ) {
      setOpenExit(true);
      return;
    }

    setTimeout(() => navigateToTab(tabIndex), 1000);
  };

  const handleExitCancel = () => setOpenExit(false);
  const handleExitClose = () => setOpenExit(false);
  const handleExitConfirm = () => {
    setOpenExit(false);
    navigateToTab(nextRoute);
  };

  const activeTab = isJobLayout && typeof tabLayout === 'undefined' ? 0 : tabLayout ?? 0;

  return {
    navItems,
    activeTab,
    handleNavClick,
    openExit,
    handleExitCancel,
    handleExitClose,
    handleExitConfirm,
    isAdmin,
  };
};
